// Firebase-backed Universal Courier Escalation service. Field-level writes only —
// never overwrites the whole escalation. Append-only audit trail + communication log.
//
// Data model:
//   courierEscalations/{id}                       the escalation record
//   courierEscalations/{id}/communications/{cid}  append-only courier comms
//   courierEscalations/{id}/auditLog/{aid}        append-only audit trail
//   courierEscalationIndex/{orderId}              → active escalation id (dedupe)
//   system_sequences/courier_escalation_sequence  CES counter
import { db } from '../../../integrations/firebase';
import { ref, push, set, get, update, onValue } from 'firebase/database';
import { recordTimelineEvent, TIMELINE_EVENTS } from '../../../core/customer/CustomerTimelineEngine';
import { normalizeCourier, extractAwb, extractCourier } from '../../orders/ui/shared/courierUtils';
import {
  priorityForReason, reasonForIssueType, REASON_LABELS, computeDueTimes,
  formatCesNumber, isActiveStatus, OWNERSHIP_LADDER, nextOwner, canTransition,
} from '../logic/courierEscalation';

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));
const actorOf = (u) => u?.displayName || u?.email || u?.uid || 'system';

export const CourierEscalationService = {
  nextEscalationNumber: async () => {
    const seqRef = ref(db, 'system_sequences/courier_escalation_sequence');
    const snap = await get(seqRef);
    const next = (snap.exists() ? (snap.val() || 0) : 0) + 1;
    await set(seqRef, next);
    return { num: next, escalationNumber: formatCesNumber(next) };
  },

  // The single active escalation for an order, or null.
  getActiveForOrder: async (orderId) => {
    if (!orderId) return null;
    const ptr = await get(ref(db, `courierEscalationIndex/${orderId}`));
    if (!ptr.exists()) return null;
    const id = ptr.val();
    const snap = await get(ref(db, `courierEscalations/${id}`));
    if (!snap.exists()) return null;
    const esc = { id, ...snap.val() };
    return isActiveStatus(esc.status) ? esc : null;
  },

  // Create a new escalation OR attach to the order's existing active one.
  // `trigger` = { reason | issueType, source, note, ticketId, vip } ; returns the escalation.
  createOrAttach: async (order, trigger = {}, currentUser) => {
    const orderId = order?.id || order?.orderId || '';
    const reason = trigger.reason || reasonForIssueType(trigger.issueType || '');
    const by = actorOf(currentUser);

    // Dedupe — one active escalation per order.
    const existing = await CourierEscalationService.getActiveForOrder(orderId);
    if (existing) {
      await CourierEscalationService.addAudit(existing.id, {
        action: 'attach', newValue: REASON_LABELS[reason] || reason, performedBy: by,
        note: trigger.source ? `Attached via ${trigger.source}` : 'Attached additional issue',
      });
      const linkUpdates = { [`courierEscalations/${existing.id}/updatedAt`]: Date.now() };
      if (trigger.ticketId) linkUpdates[`courierEscalations/${existing.id}/linkedTickets/${trigger.ticketId}`] = true;
      await update(ref(db), linkUpdates);
      return existing;
    }

    const { escalationNumber } = await CourierEscalationService.nextEscalationNumber();
    const now = Date.now();
    const vip = !!(trigger.vip || order?.customer?.customerType === 'VIP');
    const priority = trigger.priority || priorityForReason(reason, { vip });
    const due = computeDueTimes(priority, now);
    const courier = extractCourier(order) || normalizeCourier(order?.shipping?.courier) || '';

    const escRef = push(ref(db, 'courierEscalations'));
    const record = clean({
      escalationNumber,
      orderId,
      awb: extractAwb(order) || '',
      courier,
      customerName: order?.customer?.name || '',
      mobile: String(order?.customer?.mobile || '').replace(/\D/g, '').slice(-10),
      state: order?.customer?.state || '',
      district: order?.customer?.city || '',
      pincode: order?.customer?.pincode || '',
      paymentMode: order?.payment?.mode || '',
      amount: Number(order?.payment?.total || order?.totalAmount || 0),
      vip,
      reason,
      reasonLabel: REASON_LABELS[reason] || reason,
      issueType: trigger.issueType || '',
      priority,
      status: 'new',
      ownerLevel: OWNERSHIP_LADDER[0],
      assignedTo: '',
      source: trigger.source || 'manual',
      escalationCount: 0,
      responseDueAt: due.responseDueAt,
      resolutionDueAt: due.resolutionDueAt,
      slaPausedAt: 0,
      accumulatedPauseMs: 0,
      slaBreached: false,
      slaWarned: '',
      linkedTickets: trigger.ticketId ? { [trigger.ticketId]: true } : undefined,
      createdBy: by,
      createdAt: now,
      updatedAt: now,
      closedAt: 0,
    });
    await set(escRef, record);
    await update(ref(db, `courierEscalationIndex`), { [orderId]: escRef.key });

    await CourierEscalationService.addAudit(escRef.key, {
      action: 'created', newValue: `${escalationNumber} (${priority})`, performedBy: by,
      note: trigger.note || REASON_LABELS[reason] || reason,
    });
    // Auto-assign to courier executive (support ownership ends here).
    await CourierEscalationService.addAudit(escRef.key, {
      action: 'auto_assigned', newValue: OWNERSHIP_LADDER[0], performedBy: 'system',
    });
    await recordTimelineEvent(record.mobile, TIMELINE_EVENTS.COURIER_ESCALATION_CREATED, {
      escalationId: escRef.key, escalationNumber, courier, awb: record.awb,
      reason: record.reasonLabel, priority, status: 'new', orderId, updatedBy: by,
    }, by);

    return { id: escRef.key, ...record };
  },

  // Append-only audit entry.
  addAudit: async (id, { action, previousValue, newValue, performedBy, note, role, device }) => {
    const aRef = push(ref(db, `courierEscalations/${id}/auditLog`));
    await set(aRef, clean({
      action,
      previousValue: previousValue === undefined ? '' : String(previousValue),
      newValue: newValue === undefined ? '' : String(newValue),
      performedBy: performedBy || 'system',
      role: role || '', device: device || '',
      performedAt: Date.now(),
      note: note || '',
    }));
  },

  // Append-only courier communication log entry.
  addCommunication: async (id, comm, currentUser) => {
    const cRef = push(ref(db, `courierEscalations/${id}/communications`));
    const by = actorOf(currentUser);
    await set(cRef, clean({
      executive: by,
      courier: comm.courier || '',
      mode: comm.mode || 'phone', // phone | email | portal | whatsapp
      reference: comm.reference || '',
      remark: comm.remark || '',
      nextFollowUpAt: comm.nextFollowUpAt || 0,
      attachment: comm.attachment || '',
      responseTimeMins: comm.responseTimeMins || 0,
      createdAt: Date.now(),
    }));
    await update(ref(db, `courierEscalations/${id}`), { updatedAt: Date.now(), lastUpdate: Date.now() });
    await CourierEscalationService.addAudit(id, { action: 'communication', newValue: comm.mode || 'phone', performedBy: by, note: comm.remark });
  },

  // Status transition with validation + audit + timeline.
  changeStatus: async (esc, status, currentUser, note = '') => {
    if (!canTransition(esc.status, status)) throw new Error(`Cannot move ${esc.status} → ${status}`);
    const by = actorOf(currentUser);
    const now = Date.now();
    const terminal = status === 'resolved' || status === 'closed';
    // Pause SLA when waiting on customer/warehouse; resume otherwise.
    const pausing = status === 'waiting_customer' || status === 'waiting_warehouse';
    const updates = clean({
      status, updatedAt: now,
      ...(terminal ? { closedAt: now } : {}),
    });
    // SLA pause bookkeeping
    if (pausing && !esc.slaPausedAt) updates.slaPausedAt = now;
    if (!pausing && esc.slaPausedAt) {
      updates.accumulatedPauseMs = Number(esc.accumulatedPauseMs || 0) + (now - esc.slaPausedAt);
      updates.slaPausedAt = 0;
    }
    await update(ref(db, `courierEscalations/${esc.id}`), updates);
    // Drop the dedupe pointer once terminal so a future issue opens a fresh case.
    if (terminal && esc.orderId) await set(ref(db, `courierEscalationIndex/${esc.orderId}`), null);

    await CourierEscalationService.addAudit(esc.id, { action: 'status', previousValue: esc.status, newValue: status, performedBy: by, note });

    const evtMap = {
      courier_contacted: TIMELINE_EVENTS.COURIER_CONTACTED,
      courier_response: TIMELINE_EVENTS.COURIER_RESPONSE_RECEIVED,
      waiting_customer: TIMELINE_EVENTS.COURIER_WAITING_CUSTOMER || TIMELINE_EVENTS.COURIER_FOLLOWUP,
      waiting_warehouse: TIMELINE_EVENTS.COURIER_WAITING_WAREHOUSE || TIMELINE_EVENTS.COURIER_FOLLOWUP,
      reship_approved: TIMELINE_EVENTS.COURIER_RESHIP || TIMELINE_EVENTS.RESHIP,
      refund_approved: TIMELINE_EVENTS.COURIER_REFUND || TIMELINE_EVENTS.REFUND,
      resolved: TIMELINE_EVENTS.COURIER_RESOLVED,
      closed: TIMELINE_EVENTS.COURIER_CLOSED,
    };
    const evt = evtMap[status];
    if (evt) await recordTimelineEvent(esc.mobile, evt, {
      escalationId: esc.id, escalationNumber: esc.escalationNumber, courier: esc.courier,
      awb: esc.awb, status, note, orderId: esc.orderId, updatedBy: by,
    }, by);
  },

  // Assign / reassign owner (a person), keep the level.
  assign: async (esc, assignedTo, currentUser) => {
    const by = actorOf(currentUser);
    await update(ref(db, `courierEscalations/${esc.id}`), clean({
      assignedTo: assignedTo || '', status: esc.status === 'new' ? 'assigned' : esc.status, updatedAt: Date.now(),
    }));
    await CourierEscalationService.addAudit(esc.id, { action: 'assign', previousValue: esc.assignedTo, newValue: assignedTo, performedBy: by });
    await recordTimelineEvent(esc.mobile, TIMELINE_EVENTS.COURIER_ESCALATION_ASSIGNED, {
      escalationId: esc.id, escalationNumber: esc.escalationNumber, assignedTo, updatedBy: by, orderId: esc.orderId,
    }, by);
  },

  // Climb the ownership ladder (e.g. → manager → vendor escalation).
  escalateOwnership: async (esc, currentUser, note = '') => {
    const by = actorOf(currentUser);
    const level = nextOwner(esc.ownerLevel);
    await update(ref(db, `courierEscalations/${esc.id}`), clean({
      ownerLevel: level, escalationCount: Number(esc.escalationCount || 0) + 1,
      status: level === 'vendor_escalation' ? 'vendor_escalation' : esc.status, updatedAt: Date.now(),
    }));
    await CourierEscalationService.addAudit(esc.id, { action: 'escalate_owner', previousValue: esc.ownerLevel, newValue: level, performedBy: by, note });
  },

  overridePriority: async (esc, priority, currentUser) => {
    const by = actorOf(currentUser);
    const due = computeDueTimes(priority, esc.createdAt || Date.now());
    await update(ref(db, `courierEscalations/${esc.id}`), clean({
      priority, responseDueAt: due.responseDueAt, resolutionDueAt: due.resolutionDueAt, updatedAt: Date.now(),
    }));
    await CourierEscalationService.addAudit(esc.id, { action: 'priority_override', previousValue: esc.priority, newValue: priority, performedBy: by });
  },

  // Mark an SLA warning/breach as notified (idempotency guard for the engine).
  markSlaWarned: async (id, bucket) => {
    await update(ref(db, `courierEscalations/${id}`), clean({ slaWarned: bucket, slaBreached: bucket === 'breach', updatedAt: Date.now() }));
  },

  // Realtime subscription to the whole queue.
  subscribe: (cb) => onValue(ref(db, 'courierEscalations'), (snap) => {
    const val = snap.val() || {};
    cb(Object.keys(val).map(id => ({ id, ...val[id] })));
  }),
};

export default CourierEscalationService;
